import {
    useCallback,
    useEffect,
    useMemo,
    useRef
} from 'react'

import debounce from 'lodash/debounce'

import {
    postsImpression
} from 'services/endpoints/tracking'

const IMPRESSION_STORAGE_KEY = 'feed-impressions'

const getSessionImpressions = (): Set<string> => {
    if (typeof window === 'undefined') {
        return new Set()
    }

    try {
        const stored = window.sessionStorage.getItem(IMPRESSION_STORAGE_KEY)

        if (!stored) {
            return new Set()
        }

        const parsed = JSON.parse(stored)

        if (!Array.isArray(parsed)) {
            return new Set()
        }

        return new Set(parsed.map(String))
    } catch {
        return new Set()
    }
}

const saveSessionImpressions = (ids: Set<string>) => {
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.sessionStorage.setItem(
            IMPRESSION_STORAGE_KEY,
            JSON.stringify(Array.from(ids))
        )
    } catch {
        // analytics should never break the feed
    }
}

export const useImpressionsTracker = (
    debounceMs = 2000
) => {
    const impressionQueue = useRef<Set<string>>(new Set())

    /**
     * This survives route/component unmounts because sessionStorage
     * survives for the lifetime of the browser tab.
     */
    const seenImpressions = useRef<Set<string> | null>(null)

    if (seenImpressions.current === null) {
        seenImpressions.current = getSessionImpressions()
    }

    const commitImpressions = useCallback(() => {
        if (impressionQueue.current.size === 0) {
            return
        }

        const ids = Array.from(impressionQueue.current)

        /**
         * Clear first.
         *
         * This prevents the same queue being submitted twice if
         * visibilitychange + unmount occur very close together.
         */
        impressionQueue.current.clear()

        postsImpression(ids)
    }, [])

    const commitImpressionsRef = useRef(commitImpressions)

    useEffect(() => {
        commitImpressionsRef.current = commitImpressions
    }, [commitImpressions])

    const debounceImpressions = useMemo(
        () =>
            debounce(() => {
                commitImpressionsRef.current()
            }, debounceMs),
        [debounceMs]
    )

    const addDebounceImpression = useCallback(
        (
            postId: string,
            shouldFire: boolean = true
        ) => {
            if (!shouldFire || !postId) {
                return
            }

            const id = String(postId)

            if (seenImpressions.current?.has(id)) {
                return
            }

            /**
             * Mark it seen immediately rather than after the API call.
             *
             * Otherwise multiple IntersectionObserver/render events can
             * enqueue the same post before the debounce fires.
             */
            seenImpressions.current?.add(id)

            /**
             * Set also protects the pending queue against duplicate adds.
             */
            impressionQueue.current.add(id)

            if (seenImpressions.current) {
                saveSessionImpressions(seenImpressions.current)
            }

            debounceImpressions()
        },
        [debounceImpressions]
    )

    useEffect(() => {
        const flush = () => {
            commitImpressionsRef.current()
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flush()
            }
        }

        const onPageHide = () => {
            flush()
        }

        document.addEventListener(
            'visibilitychange',
            onVisibilityChange
        )

        window.addEventListener(
            'pagehide',
            onPageHide
        )

        return () => {
            /**
             * Flush whatever remains when this particular feed/component
             * disappears, but DO NOT clear seenImpressions.
             */
            debounceImpressions.cancel()

            flush()

            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange
            )

            window.removeEventListener(
                'pagehide',
                onPageHide
            )
        }
    }, [debounceImpressions])

    return useMemo(
        () => ({
            addDebounceImpression
        }),
        [addDebounceImpression]
    )
}